import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Box, Typography, Button} from 'views/components';
import {ExpandMore, ExpandLess} from 'views/icons';
import {Translate} from 'views/module';
import moment from 'moment';
import SituationCard from './components/SituationCard';

const Situation = (props) => {
  const {
    clientData: {situation},
  } = props;

  const situations = situation?.situations.sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());
  const situationSummary = situation?.situationSummary;
  const lastSituationUpdateTime = situation?.lastSituationUpdateTime;
  const newBadgeDurationHours = 24;
  const updateBadgeDurationHours = 24;
  const maxSummaryLength = 150;

  const [collapsed, setCollapsed] = React.useState('false');
  const [longSituationsCollapsed, setLongSituationsCollapsed] = React.useState(
      !situations ? null :
      situations
          .filter((situation) => situation.summary.length > maxSummaryLength)
          .reduce((acc, situation) => {
            acc[situation.situationRn] = true;
            return acc;
          }, {}),
  );

  const handleCollapseToggle = () => {
    const toggledValue = collapsed === 'true' ? 'false' : 'true';
    localStorage.setItem('collapsedSituations', toggledValue);
    setCollapsed(toggledValue);
  };
  const handleCardCollapseToggle = (situationRn) => {
    setLongSituationsCollapsed((prev) => ({
      ...prev,
      [situationRn]: !prev[situationRn],
    }));
  };

  React.useEffect(() => {
    const preference = localStorage.getItem('collapsedSituations');
    if (preference !== null) {
      setCollapsed(preference);
    }
  }, []);

  if (!situations && !situationSummary) {
    return (
      <Typography variant='subtitle2' color='textSecondary'>
        <Translate expression='client.ClientDetail.Situation.Subtitles.Empty' />
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {
        situationSummary &&
          <Box
            ml={22}
            sx={{
              pl: 2,
              borderRadius: '5px',
              borderLeft: '5px solid #e4e0e0',
            }}
          >
            <Typography sx={{color: 'text.secondary', fontStyle: 'italic', fontSize: 14}}>{situationSummary}</Typography>
          </Box>
      }
      <Stack direction='row' sx={{alignItems: 'center', gap: 1}}>
        {
          situations.length > 0 &&
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 700,
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#A8A49E',
              }}
            >
              <Translate expression='client.ClientDetail.Situation.Subtitles.Title' substitute={{numberOfSituations: situations.length}} />
            </Typography>
        }
        {
          lastSituationUpdateTime &&
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 400,
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#bcb8b1',
              }}
            >
              <Translate expression='client.ClientDetail.Situation.Subtitles.LastUpdate' substitute={{time: moment(lastSituationUpdateTime).fromNow()}}/>
            </Typography>
        }
      </Stack>
      <Button
        variant='text'
        startIcon={collapsed === 'true' ? <ExpandMore /> : <ExpandLess />}
        onClick={() => handleCollapseToggle()}
        sx={{
          display: {
            xs: situation.situations.length > 1 ? 'inline-flex' : 'none',
            md: situation.situations.length > 2 ? 'inline-flex' : 'none',
            lg: situation.situations.length > 3 ? 'inline-flex' : 'none',
          },
        }}
      >
        <Translate expression={`client.ClientDetail.Situation.Subtitles.${collapsed === 'true' ? 'Expand': 'Collapse'}`} />
      </Button>
      {
        situations.length > 0 &&
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
              gridAutoRows: 'max-content',
              alignItems: 'stretch',
              columnGap: 3,
              rowGap: 1,
              ...(collapsed === 'true' && {
                '& > div:nth-of-type(n+2)': {
                  display: {
                    xs: 'none',
                    md: 'flex',
                  },
                },
                '& > div:nth-of-type(n+3)': {
                  display: {
                    md: 'none',
                    lg: 'flex',
                  },
                },
                '& > div:nth-of-type(n+4)': {
                  display: 'none',
                },
              }),
            }}
          >
            {
              situations.map((situation) => {
                return (
                  <SituationCard
                    key={situation.situationRn}
                    clientRn={props.clientRn}
                    situation={situation}
                    onOpenClientMessage={props.onOpenClientMessage}
                    longSituationsCollapsed={longSituationsCollapsed}
                    handleCardCollapseToggle={handleCardCollapseToggle}
                    newBadgeDurationHours={newBadgeDurationHours}
                    updateBadgeDurationHours={updateBadgeDurationHours}
                    maxSummaryLength={maxSummaryLength}
                  />
                );
              })
            }
          </Box>
      }
    </Stack>
  );
};

Situation.propTypes = {
  clientBackend: PropTypes.object.isRequired,
  clientRn: PropTypes.string.isRequired,
  clientData: PropTypes.object.isRequired,
  onOpenClientMessage: PropTypes.func.isRequired,
};

export default Situation;
